// File: /home/ubuntu/avaai_dashboard/frontend/src/pages/Account.tsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../context/ThemeContext";
import ConnectionModeStatusBadge from "../components/ConnectionModeStatusBadge";
import { hardLogout } from "../utils/auth";

const API_BASE =
  import.meta.env.VITE_API_BASE ||
  "";

const TOKEN_KEYS = ["ava_token", "token", "access_token"] as const;

type MeUser = {
  sub?: string;
  id?: string | number;
  email?: string;
  role?: string;
  name?: string;
  full_name?: string;
  display_name?: string;
  username?: string;
  zip?: string;
  city?: string;
  subscription?: string;
  plan?: string;
  status?: string;
  provider?: string;
  subscription_status?: string;
  subscription_cancel_at_period_end?: boolean;
  subscription_current_period_end?: string | null;
};

type MeResponse = {
  ok?: boolean;
  user?: MeUser;
  detail?: unknown;
};

function getAnyToken(): string {
  for (const key of TOKEN_KEYS) {
    const localValue = localStorage.getItem(key);
    if (localValue && localValue.trim().length > 10) return localValue.trim();

    const sessionValue = sessionStorage.getItem(key);
    if (sessionValue && sessionValue.trim().length > 10) return sessionValue.trim();
  }
  return "";
}

function readLocalProfile() {
  const candidates = ["ava_profile", "profile", "ava_user", "user_profile", "user"];

  for (const key of candidates) {
    try {
      const raw = localStorage.getItem(key) || sessionStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        return parsed as Record<string, any>;
      }
    } catch {
      // ignore malformed storage
    }
  }

  return {};
}

function prettyValue(value?: string | null, fallback = "—"): string {
  if (!value || !String(value).trim()) return fallback;
  return String(value).trim();
}

function inferProvider(user: MeUser, localProfile: Record<string, any>): string {
  const explicit =
    user.provider ||
    localProfile.provider ||
    localProfile.auth_provider ||
    localProfile.loginProvider;

  if (explicit) return String(explicit);

  const email = (user.email || user.sub || "").toLowerCase();
  if (email.includes("gmail.com")) return "Google / Email";
  return "Email / Local";
}

function inferPlan(user: MeUser, localProfile: Record<string, any>): string {
  return (
    user.plan ||
    user.subscription_status ||
    user.subscription ||
    localProfile.plan ||
    localProfile.subscription ||
    localProfile.membership ||
    "Active"
  );
}

function inferDisplayName(user: MeUser, localProfile: Record<string, any>): string {
  return (
    user.display_name ||
    user.name ||
    user.full_name ||
    user.username ||
    localProfile.displayName ||
    localProfile.display_name ||
    localProfile.name ||
    ""
  );
}

function inferPreferredName(displayName: string, email: string): string {
  if (displayName && displayName !== "—") return displayName;
  if (email && email !== "—") {
    const first = email.split("@")[0]?.trim();
    if (first) return first;
  }
  return "Ava User";
}

const Account: React.FC = () => {
  const { themeColors } = useContext(ThemeContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<MeUser | null>(null);
  const [error, setError] = useState("");
  const [lastSync, setLastSync] = useState("");
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [refreshMessage, setRefreshMessage] = useState("");

  const localProfile = useMemo(() => readLocalProfile(), []);
  const token = useMemo(() => getAnyToken(), []);

  useEffect(() => {
    let cancelled = false;

    async function loadAccount() {
      setLoading(true);
      setError("");

      try {
        const headers: Record<string, string> = {
          "Content-Type": "application/json",
          Accept: "application/json",
        };

        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const response = await fetch(`${API_BASE}/api/v1/auth/me`, { credentials: "include",
          method: "GET",
          headers,

        });

        if (response.status === 401) {
          if (!cancelled) {
            setUser(null);
            setError("Not logged in or session expired.");
          }
          return;
        }

        const data: MeResponse = await response.json().catch(() => ({}));

        if (!response.ok || !data?.ok) {
          if (!cancelled) {
            setUser(null);
            setError(isAdmin ? "Failed to load account details from the API." : "Account sync temporarily unavailable.");
          }
          return;
        }

        if (!cancelled) {
          setUser(data.user || null);
          setLastSync(new Date().toLocaleString());
        }
      } catch {
        if (!cancelled) {
          setUser(null);
          setError("Network error while loading account details.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void loadAccount();

    return () => {
      cancelled = true;
    };
  }, [token, refreshNonce]);

  const email = prettyValue(user?.email || user?.sub || localProfile.email);
  const rawDisplayName = inferDisplayName(user || {}, localProfile);
  const preferredName = inferPreferredName(rawDisplayName, email);
  const role = prettyValue(user?.role || localProfile.role || "member");
  const zip = prettyValue(
    user?.zip ||
      (user as any)?.zip_code ||
      (user as any)?.postal_code ||
      localProfile.zip ||
      localProfile.zip_code ||
      localProfile.postal_code ||
      localProfile.profile?.zip ||
      localProfile.profile?.zip_code ||
      localProfile.address?.zip ||
      localProfile.address?.postal_code
  );
  const userId = prettyValue(
    String(
      user?.id ??
        (user as any)?.user_id ??
        user?.sub ??
        localProfile.id ??
        localProfile.user_id ??
        localProfile.sub ??
        ""
    )
  );
  const provider = prettyValue(inferProvider(user || {}, localProfile));
  const rawPlan = String(
    inferPlan(user || {}, localProfile) || ""
  ).toLowerCase();

  const normalizedRole = String(
    user?.role || localProfile.role || ""
  ).trim().toLowerCase();

  const normalizedSubscription = String(
    user?.subscription_status ||
      localProfile.subscription_status ||
      ""
  ).trim().toLowerCase();

  const offlineEntitlement = String(
    (user as any)?.offline_entitlement ||
      localProfile.offline_entitlement ||
      ""
  ).trim().toLowerCase();

  const isOfflineAdminEntitlement =
    offlineEntitlement === "admin";

  const vipLifetime = Boolean(
    (user as any)?.vip_lifetime ??
      localProfile.vip_lifetime ??
      false
  );

  const isAdmin = [
    "admin",
    "owner",
    "superadmin",
    "super_admin",
  ].includes(normalizedRole);

  const isVip =
    normalizedRole === "vip" ||
    vipLifetime;

  const isPro =
    isVip ||
    normalizedSubscription === "active" ||
    normalizedSubscription === "pro" ||
    rawPlan === "active" ||
    rawPlan === "vip" ||
    rawPlan === "pro";

  const plan =
    isAdmin
      ? "Admin"
      : isOfflineAdminEntitlement
        ? "Admin — Local Mode"
        : isPro
          ? "Pro"
          : "Free";
  const accountStatus = prettyValue(
    user?.status ||
      user?.subscription_status ||
      localProfile.status ||
      (token ? "Signed in" : "Local session")
  );

  const language = prettyValue(
    localProfile.language ||
      localProfile.locale ||
      localStorage.getItem("ava_language") ||
      sessionStorage.getItem("ava_language") ||
      "English"
  );

  const voice = prettyValue(
    localProfile.voice ||
      localProfile.voiceName ||
      localStorage.getItem("ava_voice") ||
      sessionStorage.getItem("ava_voice") ||
      "Sonia / Default"
  );

  const themeName = prettyValue(
    localProfile.theme ||
      localStorage.getItem("ava_theme") ||
      localStorage.getItem("theme") ||
      sessionStorage.getItem("ava_theme") ||
      sessionStorage.getItem("theme") ||
      "Neon"
  );

  const cancelAtPeriodEnd = !!user?.subscription_cancel_at_period_end;

  const renewalDate =
    isVip
      ? "No renewal required"
      : user?.subscription_current_period_end
        ? new Date(
            user.subscription_current_period_end
          ).toLocaleString()
        : isAdmin
          ? "Administrative access"
          : isOfflineAdminEntitlement
            ? "Administrative actions require internet"
            : isPro
              ? "Managed by Apple"
              : "Free plan";

  const apiStatus = loading ? "Checking..." : error ? "Sync unavailable" : "Connected";
  const accountIsMobile = typeof window !== "undefined" && window.innerWidth <= 820;

  const handleMembershipManagerOpen = () => {
    navigate("/subscribe");
  };

  const handleDeleteAccountWarning = () => {
    const confirmed = window.confirm(
      "Are you sure you want to continue to account deletion? This action cannot be reversed once your account is deleted."
    );

    if (confirmed) {
      navigate("/subscribe");
    }
  };

  const shellStyle: React.CSSProperties = {
    minHeight: "100%",
    width: "100%",
    padding: "18px 16px 120px",
    color: themeColors.text,
    fontFamily: "Orbitron, sans-serif",
    background: themeColors.bg,
    boxSizing: "border-box",
    overflowY: "auto",
  };

  const pageStyle: React.CSSProperties = {
    maxWidth: 1450,
    margin: "0 auto",
  };

  const compactGrid: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: accountIsMobile ? "1fr" : "1.4fr 1fr",
    gap: 16,
    alignItems: "stretch",
    marginBottom: 16,
  };

  const summaryGrid: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 12,
    marginTop: 18,
  };

  const infoGrid: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: 16,
  };

  const heroStyle: React.CSSProperties = {
    border: `1px solid ${themeColors.border}`,
    borderRadius: 24,
    padding: "22px 22px 18px",
    background: themeColors.panel,
    boxShadow: themeColors.glow,
    backdropFilter: "blur(10px)",
    minHeight: 230,
  };

  const cardStyle: React.CSSProperties = {
    border: `1px solid ${themeColors.border}`,
    borderRadius: 22,
    padding: 18,
    background: themeColors.cardBg,
    boxShadow: themeColors.glow,
    minHeight: 180,
  };

  const miniCardStyle: React.CSSProperties = {
    border: `1px solid ${themeColors.border}`,
    borderRadius: 16,
    padding: "12px 14px",
    background: themeColors.cardBg,
    minHeight: 88,
  };

  const sectionTitleStyle: React.CSSProperties = {
    color: themeColors.accent,
    margin: "0 0 14px",
    fontSize: 20,
    letterSpacing: 1.2,
    opacity: 1,
    textShadow: themeColors.glow,
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 12,
    letterSpacing: 1.15,
    textTransform: "uppercase",
    color: themeColors.mutedText,
    opacity: 1,
    fontWeight: 800,
    marginBottom: 5,
  };

  const valueStyle: React.CSSProperties = {
    fontSize: 16,
    fontWeight: 800,
    lineHeight: 1.35,
    wordBreak: "break-word",
    color: "#ffffff",
    opacity: 1,
    textShadow: "0 0 8px rgba(255,255,255,0.16)",
  };

  const pairStyle: React.CSSProperties = {
    marginBottom: 12,
  };

  const buttonRowStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: 10,
    marginTop: 12,
  };

  const buttonBase: React.CSSProperties = {
    appearance: "none",
    border: `1px solid ${themeColors.accent}`,
    background: themeColors.buttonBg,
    color: themeColors.text,
    padding: "12px 14px",
    borderRadius: 12,
    cursor: "pointer",
    fontFamily: "Orbitron, sans-serif",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0.45,
    boxShadow: `0 0 14px ${themeColors.accent}18`,
    minHeight: 46,
    width: accountIsMobile ? "100%" : undefined,
    boxSizing: "border-box",
  };

  const primaryButton: React.CSSProperties = {
    ...buttonBase,
    background: `linear-gradient(135deg, ${themeColors.accent}22, rgba(255,255,255,0.08))`,
  };

  const dangerButton: React.CSSProperties = {
    ...buttonBase,
    border: "1px solid rgba(255,120,120,0.75)",
    boxShadow: "0 0 14px rgba(255,120,120,0.18)",
    color: "#ffd7d7",
  };

  const statusBadgeStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    border: `1px solid ${themeColors.border}`,
    borderRadius: 999,
    padding: "8px 12px",
    fontSize: 12,
    color: themeColors.accent2,
    background: themeColors.buttonBg,
    marginTop: 10,
  };

  const tinyNote: React.CSSProperties = {
    fontSize: 12,
    color: themeColors.mutedText,
    opacity: 1,
    lineHeight: 1.5,
  };

  const statusOk = !error && !loading;

  return (
    <div style={shellStyle}>
      <div style={pageStyle}>
        <div style={compactGrid} className="account-top-grid">
          <section style={heroStyle}>
            <div
              style={{
                color: themeColors.accent2,
                fontSize: 12,
                letterSpacing: 2,
                textTransform: "uppercase",
                opacity: 1,
                marginBottom: 8,
                  textShadow: themeColors.glow,
                  fontWeight: 900,
              }}
            >
              AVAAIO Account Center
            </div>

            <h1
              style={{
                margin: 0,
                fontSize: "clamp(2rem, 4vw, 3.25rem)",
                color: themeColors.text,
                lineHeight: 1.02,
              }}
            >
              {preferredName}
            </h1>

            <div
              style={{
                marginTop: 12,
                fontSize: 15,
                opacity: 1,
                maxWidth: 860,
                lineHeight: 1.5,
              }}
            >
              Review your AVAAIO identity, membership, security, billing, and account tools from one clean control page.
            </div>

            <div style={statusBadgeStyle}>
              <span>●</span>
              <span>
                {loading
                  ? "Loading..."
                  : statusOk
                  ? "Signed in and synced"
                  : isAdmin ? "Needs attention" : "Sync unavailable"}
              </span>
            </div>

            <div style={summaryGrid}>
              <div style={miniCardStyle}>
                <div style={labelStyle}>Membership</div>
                <div style={valueStyle}>{loading ? "Loading..." : plan}</div>
              </div>

              <div style={miniCardStyle}>
                <div style={labelStyle}>Session</div>
                <div style={valueStyle}>{token ? "Signed In" : "Guest / Local"}</div>
              </div>

              <div style={miniCardStyle}>
                <div style={labelStyle}>Account Sync</div>
                <div style={valueStyle}>{apiStatus}</div>
              </div>

              {!isVip ? (
                <div style={miniCardStyle}>
                  <div style={labelStyle}>Renewal / End</div>
                  <div style={valueStyle}>{loading ? "Loading..." : renewalDate}</div>
                </div>
              ) : null}
            </div>

            <div style={{ marginTop: 12, ...tinyNote }}>
              {lastSync ? `Last sync: ${lastSync}` : "Waiting for account sync..."}
            </div>
          </section>

          <section style={{ ...cardStyle, minHeight: 230 }}>
            <h2 style={sectionTitleStyle}>Quick Actions</h2>

            <div style={buttonRowStyle}>
              <button style={buttonBase} onClick={() => navigate("/profile")}>
                Open Profile
              </button>


              <button style={primaryButton} onClick={handleMembershipManagerOpen}>
                Membership Manager
              </button>

              <button style={buttonBase} onClick={() => navigate("/subscribe")}>
                Billing
              </button>


              <button
                style={buttonBase}
                onClick={() => {
                  setRefreshMessage("Refreshing account sync...");
                  setRefreshNonce((n) => n + 1);
                  window.setTimeout(() => setRefreshMessage("Account sync refreshed."), 450);
                  window.setTimeout(() => setRefreshMessage(""), 2400);
                }}
              >
                Refresh Sync
              </button>

              {isAdmin ? (
                <button style={buttonBase} onClick={() => navigate("/admin")}>
                  Admin Console
                </button>
              ) : null}

              <button style={dangerButton} onClick={hardLogout}>
                Logout
              </button>
            </div>

            <div style={{ marginTop: 12, ...tinyNote }}>
              {refreshMessage ? refreshMessage : "Use Membership Manager to review subscription changes and account options."}
            </div>
          </section>


        </div>

        {error ? (
          <div
            style={{
              marginBottom: 16,
              border: "1px solid rgba(255,120,120,0.5)",
              background: "rgba(120,20,20,0.18)",
              color: "#ffd0d0",
              padding: "14px 16px",
              borderRadius: 16,
            }}
          >
            {error}
          </div>
        ) : null}

        <div style={infoGrid}>
          <section style={cardStyle}>
            <h2 style={sectionTitleStyle}>Identity</h2>

            <div style={pairStyle}>
              <div style={labelStyle}>Display Name</div>
              <div style={valueStyle}>{loading ? "Loading..." : preferredName}</div>
            </div>

            <div style={pairStyle}>
              <div style={labelStyle}>Email</div>                <div
                  style={{
                    ...valueStyle,
                    textTransform: "none",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    overflowWrap: "normal",
                    wordBreak: "normal",
                    letterSpacing: "0.01em",
                    fontSize: "clamp(0.72rem, 0.95vw, 0.92rem)",
                    maxWidth: "100%",
                  }}
                  title={email}
                >
                  {loading ? "Loading..." : email}
                </div>
            </div>

            <div>
              <div style={labelStyle}>Sign-in Provider</div>
              <div style={valueStyle}>{loading ? "Loading..." : provider}</div>
            </div>
          </section>

          <section style={cardStyle}>
            <h2 style={sectionTitleStyle}>Profile Details</h2>

            <div style={pairStyle}>
              <div style={labelStyle}>ZIP</div>
              <div style={valueStyle}>{loading ? "Loading..." : zip}</div>
            </div>

            {isAdmin ? (
              <>
                <div style={pairStyle}>
                  <div style={labelStyle}>Profile Source</div>
                  <div style={valueStyle}>
                    {Object.keys(localProfile).length > 0 ? "Local + API" : "API only"}
                  </div>
                </div>

                <div>
                  <div style={labelStyle}>User ID</div>
                  <div style={valueStyle}>
                    {loading ? "Loading..." : prettyValue(String(user?.id ?? ""))}
                  </div>
                </div>
              </>
            ) : null}
          </section>

          <section style={cardStyle}>
            <h2 style={sectionTitleStyle}>Membership</h2>

            <div style={pairStyle}>
              <div style={labelStyle}>Plan</div>
              <div style={valueStyle}>{loading ? "Loading..." : plan}</div>
            </div>

            <div style={pairStyle}>
              <div style={labelStyle}>Subscription State</div>
              <div style={valueStyle}>{loading ? "Loading..." : accountStatus}</div>
            </div>

            {!isVip ? (
              <>
                <div style={pairStyle}>
                  <div style={labelStyle}>Scheduled Cancel</div>
                  <div style={valueStyle}>
                    {loading ? "Loading..." : cancelAtPeriodEnd ? "Yes" : "No"}
                  </div>
                </div>

                <div>
                  <div style={labelStyle}>Renewal / End</div>
                  <div style={valueStyle}>{loading ? "Loading..." : renewalDate}</div>
                </div>
              </>
            ) : null}
          </section>

          <section style={cardStyle}>
            <h2 style={sectionTitleStyle}>AI Personalization</h2>

            <div style={pairStyle}>
              <div style={labelStyle}>Preferred Name</div>
              <div style={valueStyle}>{preferredName}</div>
            </div>

            <div style={pairStyle}>
              <div style={labelStyle}>Language</div>
              <div style={valueStyle}>{language}</div>
            </div>

            <div style={pairStyle}>
              <div style={labelStyle}>Voice</div>
              <div style={valueStyle}>{voice}</div>
            </div>

            <div>
              <div style={labelStyle}>Theme</div>
              <div style={valueStyle}>
                {themeName}
              </div>
            </div>
          </section>

          {/* AVAAIO_CONNECTION_MODE_ACCOUNT_START */}
          <ConnectionModeStatusBadge />
          {/* AVAAIO_CONNECTION_MODE_ACCOUNT_END */}

          <section style={cardStyle}>
            <h2 style={sectionTitleStyle}>Ava Control Center</h2>

            <div style={pairStyle}>
              <div style={labelStyle}>AI Brain</div>
              <div style={valueStyle}>{token ? "Online" : "Limited"}</div>
            </div>

            <div style={pairStyle}>
              <div style={labelStyle}>Voice Engine</div>
              <div style={valueStyle}>Ready</div>
            </div>

            <div style={pairStyle}>
              <div style={labelStyle}>Memory Engine</div>
              <div style={valueStyle}>Active</div>
            </div>

            <div>
              <div style={labelStyle}>Cloud Session</div>
              <div style={valueStyle}>{token ? "Connected" : "Offline / Local"}</div>
            </div>
          </section>

          {!isAdmin ? (
            <section style={cardStyle}>
              <h2 style={sectionTitleStyle}>Security</h2>

              <div style={pairStyle}>
                <div style={labelStyle}>Session</div>
                <div style={valueStyle}>{token ? "Signed In" : "Guest / Local"}</div>
              </div>

              <div style={pairStyle}>
                <div style={labelStyle}>Account Protection</div>
                <div style={valueStyle}>{token ? "Protected" : "Limited"}</div>
              </div>

              <div style={pairStyle}>
                <div style={labelStyle}>Cloud Session</div>
                <div style={valueStyle}>{apiStatus === "Connected" ? "Connected" : "Limited"}</div>
              </div>

              <div style={tinyNote}>
                Your session is protected. More security controls will appear here as they become available.
              </div>
            </section>
          ) : (
            <section style={cardStyle}>
              <h2 style={sectionTitleStyle}>Admin Diagnostics</h2>

              <div style={pairStyle}>
                <div style={labelStyle}>Token State</div>
                <div style={valueStyle}>{token ? "Present" : "Missing"}</div>
              </div>

              <div style={pairStyle}>
                <div style={labelStyle}>Current Access</div>
                <div style={valueStyle}>Administrator</div>
              </div>

              <div style={pairStyle}>
                <div style={labelStyle}>API Authorization</div>
                <div style={valueStyle}>
                  {token ? "Bearer token loaded" : "No active bearer token"}
                </div>
              </div>

              <div style={tinyNote}>
                Advanced diagnostics are only visible to administrators.
              </div>
            </section>
          )}

          <section style={cardStyle}>
            <h2 style={sectionTitleStyle}>Account Management</h2>

            <div style={pairStyle}>
              <div style={labelStyle}>Membership Options</div>
              <div style={valueStyle}>Manage your subscription and account settings</div>
            </div>

            <div style={pairStyle}>
              <div style={labelStyle}>Account Changes</div>
              <div style={valueStyle}>
                Open the membership page to review subscription updates or account removal options
              </div>
            </div>

            <div style={buttonRowStyle}>
              <button style={primaryButton} onClick={handleMembershipManagerOpen}>
                Open Membership Manager
              </button>

              <button style={buttonBase} onClick={() => navigate("/subscribe")}>
                Open Billing
              </button>

              <button
                style={buttonBase}
                onClick={() => {
                  try {
                    window.location.href =
                      "https://apps.apple.com/account/subscriptions";
                  } catch (err) {
                    console.error(err);
                  }
                }}
              >
                Manage Apple Subscription
              </button>

              <button
                style={buttonBase}
                onClick={async () => {
                  try {
                    alert(
                      "Restore request started. If an Apple subscription exists for this Apple ID, access may refresh automatically."
                    );

                    await fetch(
                      `${API_BASE}/api/v1/billing/apple/confirm`,
                      {
                        method: "POST",
                        credentials: "include",
                        headers: {
                          "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                          restore: true,
                        }),
                      }
                    );
                  } catch (err) {
                    console.error("Restore purchases failed:", err);
                    alert("Unable to restore purchases right now.");
                  }
                }}
              >
                Restore Purchases
              </button>

              <button style={buttonBase} onClick={() => navigate("/forgot-password")}>
                Change Password
              </button>

              <button
                style={{
                  ...buttonBase,
                  opacity: 1,
                  cursor: "not-allowed",
                }}
                onClick={() =>
                  alert(
                    "Secure email change is being prepared. New email verification, old email notification, and stable user ID memory protection will be enabled before this goes live."
                  )
                }
              >
                Change Email
              </button>

              <button style={dangerButton} onClick={handleDeleteAccountWarning}>
                Delete Account
              </button>
            </div>

            <div style={tinyNote}>
              Deleting your account is permanent and cannot be reversed once completed.
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Account;
